import React from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Button,
  Text,
  VStack,
  Box,
  Flex,
} from '@chakra-ui/react';
import { css, keyframes } from '@emotion/react';
import { getUpgradeMailtoLink } from './emailObfuscation';

// Animations
const shimmer = keyframes`
  0% { background-position: -200% center; }
  100% { background-position: 200% center; }
`;

const pulse = keyframes`
  0%, 100% { opacity: 0.4; transform: scale(1); }
  50% { opacity: 0.8; transform: scale(1.05); }
`;

const sparkle = keyframes`
  0%, 100% { opacity: 0.3; transform: scale(0.8); }
  50% { opacity: 1; transform: scale(1.2); }
`;

// Decorative orbs
const Orb = ({
  size,
  top,
  left,
  delay,
  color,
}: {
  size: number;
  top: string;
  left: string;
  delay: number;
  color: string;
}) => (
  <Box
    position="absolute"
    top={top}
    left={left}
    width={`${size}px`}
    height={`${size}px`}
    borderRadius="full"
    bg={color}
    filter="blur(20px)"
    opacity={0.5}
    css={css`
      animation: ${pulse} ${4 + delay}s ease-in-out infinite;
      animation-delay: ${delay}s;
    `}
  />
);

// Small sparkle decorations
const MiniSparkle = ({ top, right, delay }: { top: string; right: string; delay: number }) => (
  <Box
    position="absolute"
    top={top}
    right={right}
    width="4px"
    height="4px"
    borderRadius="full"
    bg="yellow.300"
    css={css`
      animation: ${sparkle} ${2 + delay}s ease-in-out infinite;
      animation-delay: ${delay}s;
      box-shadow: 0 0 8px 2px rgba(246, 224, 94, 0.6);
    `}
  />
);

interface UpgradeModalProps {
  isOpen: boolean;
  onClose: () => void;
  feature?: string;
}

export const UpgradeModal: React.FC<UpgradeModalProps> = ({
  isOpen,
  onClose,
  feature,
}) => {
  const handleUpgradeClick = () => {
    const subject = feature
      ? `Upgrade to Pro - Interested in: ${feature}`
      : undefined;
    window.location.href = getUpgradeMailtoLink(subject);
  };

  const getMessage = () => {
    if (feature) {
      return `To ${feature}, upgrade to Mailcrunch Pro.`;
    }
    return 'Unlock the full power of Mailcrunch with Pro.';
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered size="md">
      <ModalOverlay
        bg="blackAlpha.700"
        backdropFilter="blur(8px)"
        css={css`
          backdrop-filter: blur(8px);
        `}
      />
      <ModalContent
        borderRadius="16px"
        overflow="hidden"
        border="1px solid"
        borderColor="whiteAlpha.100"
        boxShadow="0 25px 50px -12px rgba(0, 0, 0, 0.5), 0 0 0 1px rgba(255, 255, 255, 0.05)"
        css={css`
          background: linear-gradient(
            135deg,
            #1a1a2e 0%,
            #16213e 25%,
            #1a1a2e 50%,
            #0f0f1a 100%
          );
        `}
      >
        {/* Decorative elements */}
        <Box position="absolute" inset={0} overflow="hidden" pointerEvents="none">
          <Orb size={120} top="-30px" left="-30px" delay={0} color="#4C51BF" />
          <Orb size={80} top="60%" left="80%" delay={1.5} color="#805AD5" />
          <Orb size={60} top="70%" left="10%" delay={0.8} color="#553C9A" />
          <MiniSparkle top="20%" right="15%" delay={0} />
          <MiniSparkle top="35%" right="25%" delay={0.5} />
          <MiniSparkle top="65%" right="12%" delay={1} />
        </Box>

        <ModalCloseButton
          color="whiteAlpha.600"
          _hover={{ color: 'white', bg: 'whiteAlpha.100' }}
          zIndex={10}
        />

        <ModalHeader
          pt={8}
          pb={2}
          textAlign="center"
          position="relative"
          zIndex={1}
        >
          <VStack spacing={3}>
            <Text
              fontSize="2xl"
              fontWeight="700"
              letterSpacing="-0.02em"
              css={css`
                background: linear-gradient(
                  135deg,
                  #ffffff 0%,
                  #e2e8f0 50%,
                  #f6e05e 100%
                );
                -webkit-background-clip: text;
                -webkit-text-fill-color: transparent;
                background-clip: text;
              `}
            >
              Upgrade to Pro
            </Text>
          </VStack>
        </ModalHeader>

        <ModalBody py={6} position="relative" zIndex={1}>
          <VStack spacing={4} align="center">
            <Text
              color="whiteAlpha.900"
              fontSize="md"
              textAlign="center"
              maxW="280px"
              lineHeight="1.6"
            >
              {getMessage()}
            </Text>
          </VStack>
        </ModalBody>

        <ModalFooter pb={8} pt={2} justifyContent="center" position="relative" zIndex={1}>
          <Button
            onClick={handleUpgradeClick}
            size="lg"
            px={8}
            height="52px"
            fontWeight="600"
            letterSpacing="-0.01em"
            borderRadius="12px"
            border="1px solid"
            borderColor="yellow.400"
            color="gray.900"
            css={css`
              background: linear-gradient(
                90deg,
                #f6e05e 0%,
                #f6ad55 25%,
                #f6e05e 50%,
                #f6ad55 75%,
                #f6e05e 100%
              );
              background-size: 200% 100%;
              animation: ${shimmer} 3s linear infinite;
              box-shadow: 0 4px 20px -4px rgba(246, 224, 94, 0.4),
                inset 0 1px 0 rgba(255, 255, 255, 0.3);

              &:hover {
                transform: translateY(-2px);
                box-shadow: 0 8px 30px -4px rgba(246, 224, 94, 0.5),
                  inset 0 1px 0 rgba(255, 255, 255, 0.4);
              }

              transition: transform 0.2s ease, box-shadow 0.2s ease;
            `}
          >
            Contact Us to Upgrade
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default UpgradeModal;
